import React, {useCallback, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {usePostHog} from 'posthog-js/react';
import css from './Table.module.scss';
import {useTableContext} from './TableContext';

const MIN_COLUMN_WIDTH = 40;

const ColumnResizeHandle = ({columnId}) => {
  const {setColumnWidth, ensureColumnsMeasured, storageKey} = useTableContext();
  const posthog = usePostHog();
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const activeDragRef = useRef(null);

  const stopDrag = useCallback(() => {
    const drag = activeDragRef.current;
    if (!drag) {
      return;
    }
    window.removeEventListener('mousemove', drag.handleMouseMove);
    window.removeEventListener('mouseup', drag.handleMouseUp);
    document.body.style.userSelect = drag.previousUserSelect;
    document.body.style.cursor = drag.previousCursor;
    activeDragRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      stopDrag();
    };
  }, [stopDrag]);

  const handleMouseDown = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    const thElement = event.currentTarget.closest('th');
    if (!thElement) {
      return;
    }

    ensureColumnsMeasured();

    startXRef.current = event.clientX;
    startWidthRef.current = thElement.getBoundingClientRect().width;

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const computeWidth = (clientX) => {
      const delta = clientX - startXRef.current;
      return Math.max(MIN_COLUMN_WIDTH, Math.round(startWidthRef.current + delta));
    };

    const handleMouseMove = (moveEvent) => {
      setColumnWidth(columnId, computeWidth(moveEvent.clientX), false);
    };

    const handleMouseUp = (upEvent) => {
      const finalWidth = computeWidth(upEvent.clientX);
      setColumnWidth(columnId, finalWidth, true);
      if (posthog && finalWidth !== Math.round(startWidthRef.current)) {
        posthog.capture('table:column_resize', {
          tableKey: storageKey,
          columnId,
          fromWidth: Math.round(startWidthRef.current),
          toWidth: finalWidth,
        });
      }
      stopDrag();
    };

    activeDragRef.current = {
      handleMouseMove,
      handleMouseUp,
      previousUserSelect,
      previousCursor,
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [columnId, setColumnWidth, ensureColumnsMeasured, stopDrag, posthog, storageKey]);

  const handleClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  return (
    <span
      className={css.resizeHandle}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize column"
    />
  );
};

ColumnResizeHandle.propTypes = {
  columnId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ColumnResizeHandle;
